/**
 * BloqueioAgendaDialog - Componente extraído do AgendamentosPage
 * Gerencia bloqueios de agenda (férias, congressos, etc.)
 * 
 * FASE 1 da refatoração - Extração de diálogos isolados
 */
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Loader2, CalendarOff, AlertCircle } from 'lucide-react';

// Tipos de bloqueio disponíveis
export const TIPOS_BLOQUEIO = [
  { value: 'bloqueio', label: 'Bloqueio Genérico' },
  { value: 'ferias', label: 'Férias' },
  { value: 'curso', label: 'Curso / Treinamento' },
  { value: 'compromisso', label: 'Compromisso Pessoal' },
  { value: 'licenca', label: 'Licença Médica' }
];

// Estado inicial do dialog
export const initialBloqueioDialogState = {
  open: false,
  loading: false,
  editingBloqueio: null
};

// Estado inicial do form
export const initialBloqueioFormState = {
  profissional_id: '',
  data_inicio: '',
  data_fim: '',
  hora_inicio: '',
  hora_fim: '',
  dia_inteiro: true,
  motivo: '',
  tipo: 'bloqueio'
};

/**
 * BloqueioAgendaDialog Component
 * 
 * @param {Object} props
 * @param {Object} props.dialogState - Estado do dialog
 * @param {Function} props.setDialogState - Setter do estado do dialog
 * @param {Object} props.formState - Estado do formulário
 * @param {Function} props.setFormState - Setter do estado do formulário
 * @param {Array} props.profissionais - Lista de profissionais
 * @param {Object} props.user - Usuário logado
 * @param {Function} props.onSave - Callback quando salva bloqueio
 */
const BloqueioAgendaDialog = ({
  dialogState,
  setDialogState,
  formState,
  setFormState,
  profissionais,
  user,
  onSave
}) => {
  
  const handleClose = () => {
    if (dialogState.loading) return;
    setDialogState(initialBloqueioDialogState);
  };

  return (
    <Dialog 
      open={dialogState.open} 
      onOpenChange={(open) => !dialogState.loading && setDialogState(prev => ({ ...prev, open }))}
    >
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle style={{ fontFamily: 'Manrope, sans-serif', color: '#2C3632' }}>
            <div className="flex items-center gap-2">
              <CalendarOff className="h-5 w-5" style={{ color: '#B75D69' }} />
              {dialogState.editingBloqueio ? 'Editar Bloqueio' : 'Bloquear Agenda'}
            </div>
          </DialogTitle>
          <DialogDescription>
            Defina o período de indisponibilidade na agenda do profissional.
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto space-y-4 py-2 pr-2">
          {/* Profissional */}
          <div className="space-y-2">
            <Label>Profissional *</Label>
            <Select
              value={formState.profissional_id || undefined}
              onValueChange={(value) => setFormState(prev => ({ ...prev, profissional_id: value }))}
              disabled={user?.perfil === 'profissional'}
            >
              <SelectTrigger data-testid="bloqueio-profissional-select">
                <SelectValue placeholder="Selecione o profissional" />
              </SelectTrigger>
              <SelectContent position="popper" sideOffset={4}>
                {profissionais.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.usuarios?.nome || 'Profissional'} - {p.especialidade}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Tipo de Bloqueio */}
          <div className="space-y-2">
            <Label>Tipo de Bloqueio</Label>
            <Select
              value={formState.tipo || undefined}
              onValueChange={(value) => setFormState(prev => ({ ...prev, tipo: value }))}
            >
              <SelectTrigger data-testid="bloqueio-tipo-select">
                <SelectValue placeholder="Selecione o tipo" />
              </SelectTrigger>
              <SelectContent position="popper" sideOffset={4}>
                {TIPOS_BLOQUEIO.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Datas */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Data Início *</Label>
              <Input
                type="date"
                value={formState.data_inicio}
                onChange={(e) => setFormState(prev => ({ ...prev, data_inicio: e.target.value }))}
                data-testid="bloqueio-data-inicio"
              />
            </div>
            <div className="space-y-2">
              <Label>Data Fim *</Label>
              <Input
                type="date"
                value={formState.data_fim}
                onChange={(e) => setFormState(prev => ({ ...prev, data_fim: e.target.value }))}
                min={formState.data_inicio}
                data-testid="bloqueio-data-fim"
              />
            </div>
          </div>
          
          {/* Dia Inteiro */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="dia_inteiro"
              checked={formState.dia_inteiro}
              onChange={(e) => setFormState(prev => ({ ...prev, dia_inteiro: e.target.checked }))}
              className="h-4 w-4 rounded border-gray-300"
              data-testid="bloqueio-dia-inteiro"
            />
            <Label htmlFor="dia_inteiro" className="text-sm cursor-pointer">
              Dia inteiro (bloqueia todos os horários)
            </Label>
          </div>
          
          {/* Horários (apenas se não for dia inteiro) */}
          {!formState.dia_inteiro && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Hora Início</Label>
                <Input
                  type="time"
                  value={formState.hora_inicio}
                  onChange={(e) => setFormState(prev => ({ ...prev, hora_inicio: e.target.value }))}
                  data-testid="bloqueio-hora-inicio"
                />
              </div>
              <div className="space-y-2">
                <Label>Hora Fim</Label>
                <Input
                  type="time"
                  value={formState.hora_fim}
                  onChange={(e) => setFormState(prev => ({ ...prev, hora_fim: e.target.value }))}
                  data-testid="bloqueio-hora-fim"
                />
              </div>
            </div>
          )}
          
          {/* Motivo */}
          <div className="space-y-2">
            <Label>Motivo (opcional)</Label>
            <Textarea
              value={formState.motivo}
              onChange={(e) => setFormState(prev => ({ ...prev, motivo: e.target.value }))}
              placeholder="Ex: Congresso de Psicologia, Férias, etc."
              rows={2}
              data-testid="bloqueio-motivo"
            />
          </div>
          
          {/* Aviso */}
          <div className="flex items-start gap-2 p-3 rounded-lg" style={{ backgroundColor: '#FEF9F3' }}>
            <AlertCircle className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: '#D4A373' }} />
            <p className="text-sm" style={{ color: '#8B7355' }}>
              Novos agendamentos não poderão ser criados neste período para o profissional selecionado.
            </p>
          </div>
        </div>
        
        <DialogFooter className="flex-shrink-0 pt-4 border-t mt-2">
          <Button 
            variant="outline" 
            onClick={handleClose}
            disabled={dialogState.loading}
          >
            Cancelar
          </Button>
          <Button 
            onClick={onSave}
            disabled={dialogState.loading}
            style={{ backgroundColor: '#B75D69' }}
            className="text-white"
            data-testid="bloqueio-submit-button"
          >
            {dialogState.loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <CalendarOff className="h-4 w-4 mr-2" />
            )}
            {dialogState.editingBloqueio ? 'Salvar Alterações' : 'Bloquear Agenda'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BloqueioAgendaDialog;
